import { ParaglideMessage } from '@inlang/paraglide-js-react';
import type { CatalogSearchResult } from '../../../data/catalogSearch';
import { Icon } from '../../../components/Icon';
import { ErrorState } from '../../../components/ErrorState';
import { LISTBOX_ID, optionId } from '../../../lib/catalogSearch';
import type { CatalogSearchState, SearchPhase } from './useCatalogSearch';
import { CatalogSearchRow } from './CatalogSearchRow';
import { CatalogSearchSkeleton } from './CatalogSearchSkeleton';
import { UseCaseSwitch, Kbd } from './CatalogSearchGuides';
import { m } from '../../../paraglide/messages';

/** A section label + right-aligned count, above each result group. */
const SectionHead = ({ title, note }: { title: string; note?: string }) => (
  <div className="flex items-baseline justify-between gap-3 px-4 pt-2.5 pb-1.5">
    <span className="mono text-11 font-semibold tracking-[0.07em] text-text-3 uppercase">
      {title}
    </span>
    {note ? <span className="mono text-11 text-text-3">{note}</span> : null}
  </div>
);

export interface CatalogSearchBodyProps {
  state: CatalogSearchState;
  /** run the semantic search for the current query (the switch affordance) */
  onRun: () => void;
  /** open a result (navigate or the onSelect override) */
  onSelect: (result: CatalogSearchResult) => void;
}

/** The phase-driven body of the palette: instant keyword rows, the searching
 *  skeleton, ranked results, or a graceful error fallback to keyword matches.
 *  The empty phase renders nothing (progressive disclosure). */
export const CatalogSearchBody = ({ state: s, onRun, onSelect }: CatalogSearchBodyProps) => {
  if (s.phase === 'empty') return null;
  if (s.loadError) {
    return (
      <div className="p-4">
        <ErrorState message={m.search_load_error()} onRetry={s.retry} />
      </div>
    );
  }
  if (s.phase === 'searching') return <CatalogSearchSkeleton />;

  const showRel = s.phase === 'results';
  const rows = (
    <div role="listbox" id={LISTBOX_ID} aria-label={m.search_endpoints()}>
      {s.rows.map((r, i) => (
        <CatalogSearchRow
          key={r.slug}
          id={optionId(r.slug)}
          result={r}
          selected={i === s.sel}
          showRelevance={showRel}
          best={showRel && i === 0}
          onSelect={() => onSelect(r)}
          onHover={() => s.setSel(i)}
        />
      ))}
    </div>
  );

  if (s.phase === 'keyword') {
    return (
      <>
        <UseCaseSwitch query={s.query.trim()} onRun={onRun} />
        {s.rows.length > 0 ? (
          <>
            <SectionHead
              title={m.search_endpoints()}
              note={
                s.rows.length === 1
                  ? m.search_match_count_one({ count: s.rows.length })
                  : m.search_match_count_other({ count: s.rows.length })
              }
            />
            {rows}
          </>
        ) : (
          <p className="px-4 py-6 text-center text-13 text-text-2">
            {m.search_keyword_empty()}
          </p>
        )}
      </>
    );
  }

  if (s.phase === 'error') {
    return (
      <>
        <div className="mx-2 mt-1 mb-1.5 flex items-start gap-2.5 rounded-10 border border-line bg-surface-2 px-3 py-2.5">
          <Icon name="search-x" size={15} className="mt-0.5 shrink-0 text-text-3" />
          <span className="text-13 text-text-2">
            {m.search_semantic_unavailable()}
          </span>
        </div>
        {s.rows.length > 0 ? rows : null}
      </>
    );
  }

  return (
    <>
      <SectionHead
        title={
          s.total === 1
            ? m.search_matched_one({ count: s.total })
            : m.search_matched_other({ count: s.total })
        }
        note={m.search_by_relevance()}
      />
      <p className="px-4 pb-1 text-12 text-text-3">
        {s.ranking === 'semantic' ? m.search_ranked_semantic() : m.search_ranked_keyword()}
      </p>
      {s.rows.length > 0 ? (
        rows
      ) : (
        <p className="px-4 py-6 text-center text-13 text-text-2">
          {m.search_results_empty()}
        </p>
      )}
    </>
  );
};

const footMeta = (phase: SearchPhase, count: number, total: number): string => {
  if (phase === 'searching') return m.search_foot_semantic();
  if (phase === 'keyword') return m.search_foot_filtering({ count });
  if (phase === 'results') return m.search_foot_results({ total, count });
  return m.search_foot_default({ count });
};

const KEYS = {
  up: () => <Kbd>&#8593;</Kbd>,
  down: () => <Kbd>&#8595;</Kbd>,
  enter: () => <Kbd>&#8629;</Kbd>,
  esc: () => <Kbd>esc</Kbd>,
};

/** The palette footer: keyboard hints (or a ranking note while searching) and a
 *  meta line with the honest endpoint count. */
export const CatalogSearchFooter = ({
  phase,
  count,
  total,
}: {
  phase: SearchPhase;
  count: number;
  total: number;
}) => (
  <div className="flex shrink-0 items-center justify-between gap-3 border-t border-line-2 bg-surface-2 px-4 py-2.5">
    {phase === 'searching' ? (
      <span className="text-12 text-text-3">{m.search_foot_ranking({ count })}</span>
    ) : (
      <div className="hidden flex-wrap items-center gap-x-3.5 gap-y-1 sm:flex">
        <span className="flex items-center gap-1.5 text-12 text-text-3">
          <ParaglideMessage message={m.search_hint_navigate} markup={KEYS} />
        </span>
        <span className="flex items-center gap-1.5 text-12 text-text-3">
          <ParaglideMessage message={m.search_hint_open} markup={KEYS} />
        </span>
        <span className="flex items-center gap-1.5 text-12 text-text-3">
          <ParaglideMessage message={m.search_hint_close} markup={KEYS} />
        </span>
      </div>
    )}
    <span className="mono text-11 whitespace-nowrap text-text-3">
      {footMeta(phase, count, total)}
    </span>
  </div>
);
